import {useEffect} from 'react';

import {Outlet} from 'react-router';

import TopNav from '../layout/top-nav/top-nav';
import ErrorHandler from '../../pages/shared/error-handler';
import {TopNavUser} from '../layout/top-nav/partials/top-nav-user';
import {TopNavTheme} from '../layout/top-nav/partials/top-nav-theme';
import {TopNavLanguage} from '../layout/top-nav/partials/top-nav-language';
import TopNavShippingStation from '../layout/top-nav/partials/top-nav-shipping-station';
import {isNavigatingTo, redirectTo} from '../routing/partials/routing-util';
import {useSessionUser} from '../../../hooks/use-session';

interface props {
    children?: React.ReactNode;
}

/**
 * The layout of a logged in user: exactly the viewport high, the top bar above the screen of the page (see `.shell` and
 * `.screen` in `assets/scss/base.scss`). The page never scrolls as a whole: a page puts its parts (a status band, its
 * content, the action bar) straight into the screen, and only what is too long scrolls, inside its own panel.
 */
export const AppAdmin = ({children}: props): React.JSX.Element => {
    const hasUser = null !== useSessionUser();

    // the session ended, also in another tab (a logout, an idle logout in development): to the login page right away,
    // not on the next request. Not `useIsLoggedIn`: the idle lock suspends the session by dropping the tokens, which
    // reads as logged out as well, while the locked terminal stays with the PIN modal; the user is kept for that.
    useEffect(() => {
        // a logout or an environment switch in this tab is already on its way there
        if (!hasUser && !isNavigatingTo('/login')) {
            redirectTo('/login');
        }
    }, [hasUser]);

    return (
        <div className="shell">
            <TopNav>
                <TopNavShippingStation />
                <TopNavLanguage />
                <TopNavTheme />
                <TopNavUser />
            </TopNav>
            <main className="screen">
                <ErrorHandler>{children ?? <Outlet />}</ErrorHandler>
            </main>
        </div>
    );
};
